import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { publishReplay, refCount, map, switchMap } from 'rxjs/operators';
import { Site } from '../../sites';
import { Kit } from './kit';
import { ColDef, GridOptions } from 'ag-grid-community';
import { KitInventoryColumns } from './kit-inventory-columns';
import { KitInventoryStorage } from './kit-inventory-storage';
import { routeSnapshotExtensions } from '../../shared/route-extensions';
import { FilterFieldValue, FilterFieldType, CovanceFilter, SerializedFilter } from '../../covance-filter';
import { MdlDialogService } from '@angular-mdl/core';

@Component({
    selector: 'kit-inventory',
    template: require('./kit-inventory.component.html'),
    styles: [require('./kit-inventory.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KitInventoryComponent {

    public kits$: Observable<Kit[]>;
    public columns: ColDef[];
    public siteId: number;
    public filterFields$: Observable<FilterFieldValue[]>;
    public gridOptions: GridOptions;
    public filtersExportData: Observable<SerializedFilter[]>;
    public quickSearchSubject$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    constructor(
        route: ActivatedRoute,
        private kitInventoryColumns: KitInventoryColumns,
        private kitInventoryStorage: KitInventoryStorage,
        private readonly dialogService: MdlDialogService,
        private readonly filterService: CovanceFilter
    ) {
        const site = routeSnapshotExtensions.findData(route.snapshot, 'selectedSite') as Site | null;
        this.siteId = site && site.siteId;

        this.columns = this.kitInventoryColumns.investigatorColumns;

        this.filterFields$ = this.kitInventoryStorage.loadFilterItems(this.siteId).pipe(
            map(filterItems => {
                const filterDefs: FilterFieldValue[] = this.kitInventoryColumns.filterDefinitions;
                this.filterService.setFiltersItems(filterItems, filterDefs);
                return filterDefs;
            }),
            publishReplay(1),
            refCount());

        const filterFlow$ = this.filterFields$
            .pipe(
                switchMap(fields => this.filterService.listen(fields).pipe(map(() => fields))),
                publishReplay(1),
                refCount()
            );

        this.kits$ = filterFlow$
            .pipe(
                switchMap((fields) => {
                    this.showLoadingOverlay();
                    return this.kitInventoryStorage.getKits(this.siteId, fields);
                }),
                publishReplay(1),
                refCount()
        ) as Observable<Kit[]>;

        this.gridOptions = {
            rowHeight: 35,
        };

        this.filtersExportData = combineLatest([filterFlow$, this.quickSearchSubject$])
            .pipe(
                map(([fieldValues, quickSearchValue]) => {
                    const filtersExportData = this.filterService.serializeFieldValues(fieldValues);
                    filtersExportData.unshift({ title: 'Search', value: quickSearchValue });

                    return filtersExportData;
                }),
            );
    }

    public showHelpModal() {
        var helpText = this.kitInventoryStorage.getHelpText();

        this.dialogService.alert(helpText, 'Close', 'Column Descriptions');
    }

    public onQuickSearchChanged(value: string) {
        this.quickSearchSubject$.next(value);
    }

    private showLoadingOverlay() {
        if (this.gridOptions && this.gridOptions.api) {
            this.gridOptions.api.showLoadingOverlay();
        }
    }
}